/**
 * Web component for Anear choice group (like radio buttons but with custom styling)
 *
 * Usage:
 *   <anear-choice-group name="face" value="5">
 *     <div class="choice" value="1">Choice 1</div>
 *     <div class="choice" value="2">Choice 2</div>
 *     <div class="choice" value="3">Choice 3</div>
 *   </anear-choice-group>
 *
 * Each child element with a 'value' attribute is considered a selectable choice.
 * Clicking a choice updates the component's value and the hidden input for form integration.
 * The component adds a 'selected' class to the active choice.
 */
class AnearChoiceGroup extends HTMLElement {
  constructor() {
    super()
    this._value = null
    this._hiddenInput = null
    this._boundChoiceClickHandler = null
  }

  static get observedAttributes() {
    return ['name', 'value']
  }

  connectedCallback() {
    // Read initial value attribute
    const valueAttr = this.getAttribute('value')
    if (valueAttr !== null && this._value === null) {
      this._value = valueAttr
    }

    // Create hidden input for form integration if it doesn't exist
    if (!this._hiddenInput) {
      this._hiddenInput = document.createElement('input')
      this._hiddenInput.type = 'hidden'
      const nameAttr = this.getAttribute('name')
      if (nameAttr) {
        this._hiddenInput.setAttribute('name', nameAttr)
      }
      this.insertBefore(this._hiddenInput, this.firstChild)
    }

    // Update hidden input value
    if (this._hiddenInput && this._value !== null) {
      this._hiddenInput.value = this._value
    }

    // Set up choice click handlers (use setTimeout to ensure DOM is ready)
    // This handles cases where children might be added after connectedCallback
    setTimeout(() => {
      this.setupChoiceHandlers()
      this.updateSelection()
    }, 0)
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this._boundChoiceClickHandler) {
      this.querySelectorAll('[value], [data-choice-value]').forEach(choice => {
        choice.removeEventListener('click', this._boundChoiceClickHandler)
      })
      this._boundChoiceClickHandler = null
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'value') {
      this._value = newValue
      if (this._hiddenInput) {
        this._hiddenInput.value = newValue || ''
      }
      this.updateSelection()
    } else if (name === 'name' && this._hiddenInput) {
      this._hiddenInput.setAttribute('name', newValue || '')
    }
  }

  get value() {
    return this._value !== null ? this._value : (this.getAttribute('value') || '')
  }

  set value(newValue) {
    this._value = String(newValue)
    this.setAttribute('value', this._value)
    if (this._hiddenInput) {
      this._hiddenInput.value = this._value
    }
    this.updateSelection()
  }

  setupChoiceHandlers() {
    // Remove existing handlers if any
    if (this._boundChoiceClickHandler) {
      this.querySelectorAll('[value], [data-choice-value]').forEach(choice => {
        choice.removeEventListener('click', this._boundChoiceClickHandler)
      })
    }

    // Create new handler
    this._boundChoiceClickHandler = (e) => {
      e.preventDefault()
      e.stopPropagation()
      const choice = e.currentTarget
      const choiceValue = choice.getAttribute('value') || choice.getAttribute('data-choice-value')
      if (choiceValue !== null) {
        this.value = choiceValue
        // Dispatch a change event for potential external listeners
        this.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          detail: { value: choiceValue }
        }))
      }
    }

    // Attach handler to all choices
    this.querySelectorAll('[value], [data-choice-value]').forEach(choice => {
      choice.addEventListener('click', this._boundChoiceClickHandler)
      // Make choices appear clickable (if not already styled)
      if (choice.style.cursor === '') {
        choice.style.cursor = 'pointer'
      }
    })
  }

  updateSelection() {
    const currentValue = this.value

    // Remove selected class from all choices
    this.querySelectorAll('[value], [data-choice-value]').forEach(choice => {
      choice.classList.remove('selected')
    })

    // Add selected class to matching choice
    if (currentValue) {
      this.querySelectorAll('[value], [data-choice-value]').forEach(choice => {
        const choiceValue = choice.getAttribute('value') || choice.getAttribute('data-choice-value')
        if (choiceValue === currentValue) {
          choice.classList.add('selected')
        }
      })
    }
  }

  // Handle dynamic addition of choices (e.g., via MutationObserver)
  // For now, we'll rely on setupChoiceHandlers being called after DOM updates
  // Components should call setupChoiceHandlers if they dynamically add choices
}

// Register the custom element
if (!customElements.get('anear-choice-group')) {
  customElements.define('anear-choice-group', AnearChoiceGroup)
}

export default AnearChoiceGroup

